import React from 'react';
import { useTranslation } from 'react-i18next';
import type { KBDocumentDetails } from '../services/knowledgeBaseService';
import type { DerivativeFunction } from '../hooks/useDocumentDerivatives';
import type { OriginalFetchJob } from '../services/documentFetchService';
import OriginalFetchProgress from './OriginalFetchProgress';

interface DerivativesDropdownProps {
  doc: KBDocumentDetails;
  functions: DerivativeFunction[];
  isProcessing: boolean;
  hasDerivative: (documentId: string, derivativeType: string) => boolean;
  onExecute: (doc: KBDocumentDetails, functionName: string) => void;
  placeholder: string;
  className?: string;
  onOpen?: (documentId: string) => void;
  fetchJob?: OriginalFetchJob | null;
  onCancelFetch?: () => void;
}

/**
 * Derivatives action menu for a KB document row. The collapsed control shows the
 * placeholder; the placeholder option is hidden so it does not repeat as a dead
 * first line inside the open menu. Behaviour lives in useDocumentDerivatives.
 * While a background original-PDF fetch runs for this document, a thin progress
 * bar renders under the menu (other functions stay usable).
 */
const DerivativesDropdown: React.FC<DerivativesDropdownProps> = ({
  doc, functions, isProcessing, hasDerivative, onExecute, placeholder, className, onOpen, fetchJob, onCancelFetch,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <select
        className={className}
        value=""
        disabled={isProcessing}
        onChange={e => { onExecute(doc, e.target.value); e.target.value = ''; }}
        onFocus={() => onOpen?.(doc.id)}
      >
        <option value="" hidden>
          {isProcessing ? t('kbDocuments.processing') : placeholder}
        </option>
        {functions.map(func => (
          <option key={func.value} value={func.value}>
            {hasDerivative(doc.id, func.value) ? `✓ ${func.label}` : func.label}
          </option>
        ))}
      </select>
      {fetchJob && onCancelFetch && <OriginalFetchProgress job={fetchJob} onCancel={onCancelFetch} />}
    </>
  );
};

export default DerivativesDropdown;
